import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import { ownerIds } from 'src/components/form';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import { useInsertImpact } from '@/hooks/mutations/impact/useInsertImpact';
import { useGetImpacts } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { getCounter } from '@/utils/collectionUtils';

import { useGetCollectionTableProps } from './config';
import ImpactModal from './ImpactModal';
import { ImpactRegisterToolbar } from './ImpactRegisterToolbar';

const Page: FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { t: st } = useTranslation(['common'], { keyPrefix: 'impacts' });
  const { data, loading, refetch } = useGetImpacts({
    queryArgs: {},
  });

  const { insertImpact } = useInsertImpact();
  const openCreate = useCallback(() => setIsModalVisible(true), []);

  const tableProps = useGetCollectionTableProps(data?.impact);

  const counter = getCounter(tableProps.totalItemsCount, loading);
  const title = st('register_title');

  return (
    <PageLayout
      helpTranslationKey={'impacts.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-155) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <ImpactRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            onCreate={openCreate}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton tableProps={tableProps} entityLabel={title} />
          <Permission permission={'insert:impact'}>
            <Button variant={'primary'} onClick={openCreate}>
              {st('create_new_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <Table {...tableProps} loading={loading} />
      {isModalVisible && (
        <ImpactModal
          onDismiss={() => setIsModalVisible(false)}
          onSaving={async (data) => {
            await insertImpact({
              RatingGuidance: data.RatingGuidance,
              Rationale: data.Rationale,
              Name: data.Name,
              LikelihoodAppetite: data.LikelihoodAppetite,
              ...ownerIds(data),
            });
            refetch();
          }}
        />
      )}
    </PageLayout>
  );
};

export default Page;
